import { useEffect, useState, type ReactNode } from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';

export type BadgeVariant =
  | 'default'
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'outline'
  | 'teal'
  | 'amber';

const badgeStyles: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-600',
  success: 'bg-emerald-950/50 text-emerald-400 border border-emerald-800',
  warning: 'bg-amber-950/50 text-amber-400 border border-amber-800',
  error: 'bg-red-950/50 text-red-400 border border-red-800',
  info: 'bg-blue-950/50 text-blue-400 border border-blue-800',
  outline: 'border border-gray-700 text-gray-400',
  teal: 'bg-brand/15 text-brand',
  amber: 'bg-brand-accent/15 text-amber-400',
};

export function Badge({
  variant = 'default',
  children,
  className = '',
}: {
  variant?: BadgeVariant;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${badgeStyles[variant]} ${className}`}
    >
      {children}
    </span>
  );
}

export function StockHealth({
  stock,
  min,
  compact = false,
}: {
  stock: number;
  min: number;
  compact?: boolean;
}) {
  if (stock === 0) {
    return (
      <Badge variant="error">
        <XCircle size={10} />
        {!compact && 'Rupture'}
      </Badge>
    );
  }
  if (stock < min * 0.5) {
    return (
      <Badge variant="error">
        <AlertTriangle size={10} />
        {!compact && 'Critique'}
      </Badge>
    );
  }
  if (stock < min) {
    return (
      <Badge variant="warning">
        <AlertTriangle size={10} />
        {!compact && 'Bas'}
      </Badge>
    );
  }
  return (
    <Badge variant="success">
      <CheckCircle size={10} />
      {!compact && 'OK'}
    </Badge>
  );
}

export function ComingLaterBanner({ feature }: { feature?: string }) {
  return (
    <div className="mb-4 flex items-start gap-3 rounded-xl border border-brand-accent/40 bg-brand-accent/10 px-4 py-3">
      <Info size={16} className="text-brand-accent shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-semibold text-amber-300">Bientôt disponible…</p>
        <p className="text-xs text-gray-400 mt-0.5">
          {feature
            ? `${feature} — interface conforme au design Figma ; intégration backend prévue prochainement.`
            : 'Cette section correspond au design Figma ; la logique métier complète sera connectée prochainement.'}
        </p>
      </div>
    </div>
  );
}

export function fmtN(n: number) {
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function LiveClock({
  className = '',
  showSeconds = true,
}: {
  className?: string;
  showSeconds?: boolean;
}) {
  const [t, setT] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setT(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <span className={`tabular-nums text-xs xl:text-sm font-medium ${className}`}>
      {t.toLocaleTimeString('fr-TN', {
        hour: '2-digit',
        minute: '2-digit',
        ...(showSeconds ? { second: '2-digit' as const } : {}),
      })}
    </span>
  );
}
